import Vue from 'vue'
import md5 from 'md5'
import i18n from '@ms/lang/i18n.js'
import UIDialog from './UIDialog.vue'

const DialogConstructor = Vue.extend(UIDialog)

const dialogMap = {}

/**
 * Opens a new dialog instance with the provided properties.
 *
 * @param {Object} [propsData={}] - The properties to pass to the dialog instance.
 * @param {Boolean} [propsData.isWeb] - is web dialog style, default () => /web/.test(location.pathname) || document.documentElement.clientWidth >= 768
 * @param {String} [propsData.title] - dialog title
 * @param {String} [propsData.message] - dialog message
 * @param {String} [propsData.cancelButtonText] - dialog cancelButtonText
 * @param {String} [propsData.confirmButtonText] - dialog confirmButtonText
 * @param {Boolean} [propsData.confirmLoading] - dialog confirm button loading
 * @param {Boolean} [propsData.showClose] - dialog close button visibility
 * @param {Array} [propsData.classNames] - dialog class names
 * @param {Boolean} [propsData.dangerouslyUseHTMLString] - is html string message
 * @param {Boolean} [propsData.closeOnClickModal] - can close dialog by clicking modal
 * @param {String} [propsData.direction] - footer buttons direction, default 'horizontal', support 'vertical'
 * @param {String} [propsData.maxWidth] - .ui-dialog style max-width
 * @param {Function} [propsData.onBeforeClose] - Method to callback before close the dialog
 * @param {Function} [propsData.onClose] - Method to callback on click close button
 * @param {Function} [propsData.onConfirm] - Method to callback on click confirm button
 * @param {Function} [propsData.onCancel] - Method to callback on click cancel button
 * @returns {Object} An object representing the dialog with methods to open, close, and remove it.
 * @property {Object} instance - The dialog instance.
 * @property {Function} open - Method to open the dialog.
 * @property {Function} close - Method to close the dialog.
 * @property {Function} remove - Method to remove the dialog from the DOM.
 */
export const openDialog = (propsData, { vnodeProps = {} } = {}) => {
  const hash = md5(JSON.stringify(propsData))
  let message = propsData.message
  const currentDialog = dialogMap[hash]
  if (currentDialog) {
    currentDialog.open()
    return currentDialog
  }

  const messageIsComponent = typeof message === 'object' && !!message.render
  const removeDom = ($el, hash) => {
    if ($el.parentNode) {
      $el.parentNode.removeChild($el)
      delete dialogMap[hash]
    }
  }
  const instance = new DialogConstructor({
    propsData: {
      ...propsData,
      message: messageIsComponent ? '' : message,
      onClose:
        propsData.onClose ||
        (() => {
          removeDom(instance.$el, hash)
        }),
      onConfirm:
        propsData.onConfirm ||
        (() => {
          removeDom(instance.$el, hash)
        }),
      onCancel:
        propsData.onCancel ||
        (() => {
          removeDom(instance.$el, hash)
        })
    }
  })
  instance.$mount()

  if (messageIsComponent) {
    const messageConstructor = Vue.extend(message)
    const vnodeMessage = new messageConstructor({ i18n, ...vnodeProps }).$mount()
    instance.$refs.main.appendChild(vnodeMessage.$el)
  }

  const { $el } = instance
  document.querySelector('body').appendChild($el)

  const dialog = {
    instance,
    hash,
    open() {
      instance.open()
    },
    close() {
      instance.handleClose()
    },
    remove() {
      removeDom($el, hash)
    }
  }

  dialogMap[hash] = dialog
  dialog.open()
  return dialog
}

/**
 * Closes the specified dialog instance or all dialog instances in the message queue.
 *
 * @param {Object|null} instance - The dialog instance to close. If null, all dialogs in the queue will be closed.
 */
export const closeDialog = (instance = null) => {
  if (instance && dialogMap[instance.hash]) {
    instance.close()
    return
  }

  Object.values(dialogMap).forEach((dialog) => {
    dialog.close()
    dialog.remove()
  })
}

export default UIDialog
